import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface EzremsTransitionPreview {
  room_id: string;
  unit_ids: string[];
  sale_revision: number;
  sale_product: string | null;
  pending: boolean;
  contract_status:
    | 'UNKNOWN'
    | 'UNBOUND'
    | 'OBSERVED_NO_FUTURE'
    | 'FUTURE_CONTRACT'
    | 'DRAFT_CONTRACT';
  eligible: boolean;
  current_end: string | null;
  earliest_date: string | null;
  provider_kind: string | null;
  write_supported: boolean;
  write_unavailable_reason: 'UNBOUND' | 'UNSUPPORTED_TARGET' | null;
}
const keys = [
  'room_id',
  'unit_ids',
  'sale_revision',
  'sale_product',
  'pending',
  'contract_status',
  'eligible',
  'current_end',
  'earliest_date',
  'provider_kind',
  'write_supported',
  'write_unavailable_reason',
];
const nullableString = (v: unknown) => v === null || typeof v === 'string';
const date = (v: unknown) =>
  v === null ||
  (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && isOffsetDateTime(`${v}T00:00:00Z`));
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '현재 계약과 전환 준비 정보를 확인하지 못했습니다.',
  });
export async function readEzremsTransitionPreview(
  client: ServiceClient,
  roomId: string,
  signal?: AbortSignal,
): Promise<EzremsTransitionPreview> {
  if (!isUuid(roomId)) throw invalid();
  const value = await client.get<unknown>(`admin/rooms/${roomId}/ezrems-transition-preview`, {
    headers: { 'Cache-Control': 'no-store' },
    signal,
  });
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw invalid();
  const v = value as Record<string, unknown>;
  if (
    Object.keys(v).length !== keys.length ||
    !keys.every((k) => Object.hasOwn(v, k)) ||
    !isUuid(v.room_id) ||
    v.room_id.toLowerCase() !== roomId.toLowerCase() ||
    !Array.isArray(v.unit_ids) ||
    v.unit_ids.length === 0 ||
    !v.unit_ids.every(isUuid) ||
    new Set(v.unit_ids.map((id: string) => id.toLowerCase())).size !== v.unit_ids.length ||
    typeof v.sale_revision !== 'number' ||
    !Number.isSafeInteger(v.sale_revision) ||
    v.sale_revision < 0 ||
    !nullableString(v.sale_product) ||
    typeof v.pending !== 'boolean' ||
    typeof v.eligible !== 'boolean' ||
    typeof v.write_supported !== 'boolean' ||
    typeof v.contract_status !== 'string' ||
    !['UNKNOWN', 'UNBOUND', 'OBSERVED_NO_FUTURE', 'FUTURE_CONTRACT', 'DRAFT_CONTRACT'].includes(
      v.contract_status,
    ) ||
    !date(v.current_end) ||
    !date(v.earliest_date) ||
    !nullableString(v.provider_kind) ||
    (v.write_unavailable_reason !== null &&
      v.write_unavailable_reason !== 'UNBOUND' &&
      v.write_unavailable_reason !== 'UNSUPPORTED_TARGET')
  )
    throw invalid();
  return v as unknown as EzremsTransitionPreview;
}
